import { Show } from "solid-js";
import { Button } from "~/common/button";
import { Icon } from "~/icon";
import { formatUSD } from "~/util/format";
import type { PatientT } from "~/patient_i18n/i18n";

// The enterprise preview renders without the patient pages' strings: their English.
const ENGLISH: Record<string, string> = {
  "add_on.add": "Add to my visit",
  "add_on.added": "Added",
  "add_on.ask_about_pricing": "Ask us about pricing",
  "add_on.discounted_price": "%{price} when you add it today",
  "add_on.per_appointment": "(per appointment)",
};

const englishT: PatientT = (key, vars = {}) =>
  (ENGLISH[key] ?? key).replace(/%\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );

// The patient-facing add-on offer card, exactly as the visit hub renders it.
// Shared by the hub page (with the real accept wiring) and the enterprise
// add-on dialog's live preview, so the preview can't drift from what patients
// actually see. Purely presentational: the accept behavior comes in via onAdd.
export function AddOnCard(props: {
  name: string;
  description: string | null;
  priceCents: number | null;
  discountCents: number | null;
  added?: boolean;
  adding?: boolean;
  onAdd?: () => void;
  // Family view on the hub: the price is charged per appointment, so the card
  // says so. Omitted everywhere else (single-patient hub, enterprise preview).
  perAppointment?: boolean;
  // The hub's translated strings; the enterprise preview leaves it out.
  t?: PatientT;
}) {
  const t = () => props.t ?? englishT;

  return (
    <div class="flex flex-col gap-3 rounded-xl border border-brand-subtle p-4">
      <div>
        <p class="font-medium text-typography-primary">{props.name}</p>
        <Show when={props.description}>
          {(description) => <p class="mt-0.5 text-sm text-typography-secondary">{description()}</p>}
        </Show>
        <p class="mt-1.5 text-sm text-typography-primary">
          <AddOnPrice
            priceCents={props.priceCents}
            discountCents={props.discountCents}
            perAppointment={props.perAppointment}
            t={t()}
          />
        </p>
      </div>

      <Show
        when={!props.added}
        fallback={
          <div class="flex h-input items-center justify-center gap-1.5 rounded-full bg-brand-success-light font-medium text-brand-success-dark">
            <Icon name="check" size="sm" />
            {t()("add_on.added")}
          </div>
        }
      >
        <Button
          type="button"
          variant="secondary"
          width="full"
          leftIconName="plus"
          loading={props.adding}
          disabled={props.adding}
          onClick={() => props.onAdd?.()}
        >
          {t()("add_on.add")}
        </Button>
      </Show>
    </div>
  );
}

function AddOnPrice(props: {
  priceCents: number | null;
  discountCents: number | null;
  perAppointment?: boolean;
  t: PatientT;
}) {
  // A configured $0 price is a real price ("free"), so gate on null — a keyed
  // Show would swallow 0 as falsy and misrender it as "Ask us about pricing".
  const price = () => props.priceCents ?? 0;
  return (
    <Show when={props.priceCents != null} fallback={<>{props.t("add_on.ask_about_pricing")}</>}>
      <Show when={props.discountCents} fallback={<>{formatPrice(price())}</>}>
        {(discount) => (
          <>
            <span class="text-typography-secondary line-through">{formatPrice(price())}</span>{" "}
            <span class="font-medium text-brand-success-dark">
              {props.t("add_on.discounted_price", { price: formatPrice(price() - discount()) })}
            </span>
          </>
        )}
      </Show>
      <Show when={props.perAppointment}>
        <span class="text-typography-secondary"> {props.t("add_on.per_appointment")}</span>
      </Show>
    </Show>
  );
}

function formatPrice(cents: number): string {
  return formatUSD(cents, { fromCents: true, style: cents % 100 === 0 ? "dollars" : "cents" });
}
